import axios from 'axios'
import { isNavigationFailure, NavigationFailureType } from 'vue-router'
import logger from '@/utils/logger'
import { checkOrgDeleted, checkUserDeleted } from '@/utils/orgCheck'

const service = axios.create({
  baseURL: process.env.api_url_dev, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 5000, // request timeout
  withCredentials: false,
})

// Бэкенд ротирует refresh-токен: успешный /auth/refresh гасит предыдущий.
// Если несколько параллельных запросов словили 401 и каждый пойдёт обновляться
// сам, все кроме первого придут с уже погашенным токеном, получат 403 и
// уронят сессию. Поэтому обновление всегда одно на всех ожидающих.
let refreshRequest = null

function runTokenRefresh() {
  if (refreshRequest) {
    return refreshRequest
  }

  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    return Promise.reject(new Error('Нет refresh-токена'))
  }

  refreshRequest = service
    .post('/auth/refresh', { refreshToken }, { _skipAuthRefresh: true })
    .then(({ data }) => {
      localStorage.setItem('accessToken', data.accessToken)
      localStorage.setItem('refreshToken', data.refreshToken)
      return data.accessToken
    })
    .finally(() => {
      refreshRequest = null
    })

  return refreshRequest
}

function clearSessionAndRedirect() {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
  localStorage.setItem('loggedIn', 'false')

  // Редирект на страницу авторизации
  if (window?.$nuxt !== undefined && window?.$nuxt !== null) {
    window.$nuxt.$router.push('/auth').catch((e) => {
      // Уже на /auth или навигацию перебил гвард — не роняем промис в консоль
      if (isNavigationFailure(e, NavigationFailureType.duplicated)) return
      logger.error('Не удалось перейти на /auth:', e)
    })
  }
}

service.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = 'Bearer ' + token
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (originalRequest?._skipAuthRefresh) {
      return Promise.reject(error)
    }

    // Удалённая орг или пользователь (админ удалил) — сброс сессии и редирект на /auth
    const context = {}
    if (checkOrgDeleted(error, context) || checkUserDeleted(error, context)) {
      return Promise.reject(error)
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      try {
        const accessToken = await runTokenRefresh()

        originalRequest.headers.Authorization = 'Bearer ' + accessToken

        return service(originalRequest)
      } catch (refreshError) {
        logger.error('Не удалось обновить токен, сброс сессии:', refreshError)
        clearSessionAndRedirect()

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default service
